"use client";

import { useRef, useState } from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Field, describedBy, fieldClass, validators } from "@/components/sections/misc/form-field";
import { useForm } from "@/components/sections/misc/use-form";

/** Contact-sales form for /enterprise. Client-side only; the success state promises only the reply time. */
export function SalesForm() {
  const [sent, setSent] = useState(false);
  const doneRef = useRef<HTMLHeadingElement>(null);
  const form = useForm(
    { name: "", email: "", company: "", seats: "", message: "" },
    {
      name: validators.name,
      email: validators.email,
      company: validators.company,
      seats: validators.seats,
    },
    () => {
      setSent(true);
      requestAnimationFrame(() => doneRef.current?.focus());
    },
  );
  const { errors, bind, values } = form;

  if (sent) {
    return (
      <div role="status" className="flex min-h-[24rem] flex-col justify-center">
        <h3 ref={doneRef} tabIndex={-1} className="text-h3 text-navy">
          Thanks. We reply within one business day.
        </h3>
        <p className="mt-3 max-w-narrow text-pretty text-body text-slate">
          We will write to {values.email.trim()} about a rollout at {values.company.trim()} ({values.seats.trim()} {Number(values.seats) === 1 ? "seat" : "seats"}).
        </p>
        <div className="mt-8">
          <Button
            variant="secondary"
            onClick={() => {
              form.reset();
              setSent(false);
            }}
          >
            Send another message
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form noValidate onSubmit={form.onSubmit} aria-label="Contact sales">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field id="sales-name" label="Name" error={errors.name}>
          <input
            id="sales-name"
            type="text"
            autoComplete="name"
            className={fieldClass(!!errors.name)}
            aria-describedby={describedBy("sales-name", errors.name)}
            {...bind("name")}
          />
        </Field>
        <Field id="sales-email" label="Work email" error={errors.email}>
          <input
            id="sales-email"
            type="email"
            autoComplete="email"
            inputMode="email"
            className={fieldClass(!!errors.email)}
            aria-describedby={describedBy("sales-email", errors.email)}
            {...bind("email")}
          />
        </Field>
        <Field id="sales-company" label="Company" error={errors.company}>
          <input
            id="sales-company"
            type="text"
            autoComplete="organization"
            className={fieldClass(!!errors.company)}
            aria-describedby={describedBy("sales-company", errors.company)}
            {...bind("company")}
          />
        </Field>
        <Field id="sales-seats" label="Seats" hint="People who will log in, including viewers." error={errors.seats}>
          <input
            id="sales-seats"
            type="text"
            inputMode="numeric"
            className={fieldClass(!!errors.seats)}
            aria-describedby={describedBy("sales-seats", errors.seats, true)}
            {...bind("seats")}
          />
        </Field>
        <Field
          id="sales-message"
          label="Message"
          optional
          hint="Your timeline, security review needs or anything else we should know."
          className="sm:col-span-2"
        >
          <textarea
            id="sales-message"
            rows={4}
            className={fieldClass(false, "resize-y")}
            aria-describedby={describedBy("sales-message", undefined, true)}
            {...bind("message")}
          />
        </Field>
      </div>
      <div className="mt-7 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <Button type="submit" size="lg">
          Contact sales
        </Button>
        <p className="text-small text-slate">
          We use your details only to reply. See the{" "}
          <Link href="/privacy" className="prose-link">
            privacy policy
          </Link>
          .
        </p>
      </div>
    </form>
  );
}
